import React from 'react'
import { Box, Button, Grid, Typography } from '@mui/material'
import { useQueryClient } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { object, string } from 'yup'

import { Dialog, useSnackbarContext } from '../../../components'
import {
  Form,
  FormSchema,
  FormSchemaMessages,
  FormSchemaTests,
  InputNormalizers,
  RadioGroupControl,
  SubmitButton,
  TextFieldControl,
} from '../../../components/form'
import { ApiErrorResponse, CreateOperationRequestParams, CreateTemplateRequestParams } from '../../../types.ts'
import { TEMPLATES_QUERY_KEYS, useMutateCreateTemplate } from '../../../services'
import { OfdFields } from '../../operation-requests/ofd-fields.tsx'
import { useDialog } from '../../../hooks'
import { useUser } from '@/stores/slices/user-slice.ts'

const TEMPLATE_FORM = 'templatePaymentForm'
const templateFormValidationSchema = object<FormSchema<CreateTemplateRequestParams>>({
  paymentInfoShort: string().required(FormSchemaMessages.required),
  amount: string().required(FormSchemaMessages.required).test(FormSchemaTests.isNotZero),
  paymentCountLimit: string().required(FormSchemaMessages.required).test(FormSchemaTests.isNotZero),
  validDays: string().required(FormSchemaMessages.required).test(FormSchemaTests.isNotZero),
  validHours: string().test(FormSchemaTests.isWithinRange(0, 24)),
  validMinutes: string().test(FormSchemaTests.isWithinRange(0, 60)),

  ofdPaymentMethodType: string().when(['ofdBillMode'], ([ofdBillMode], schema) =>
    ofdBillMode === 'MANUAL' ? schema : schema.required(FormSchemaMessages.required)
  ),
  ofdPaymentSubjectType: string().when(['ofdBillMode'], ([ofdBillMode], schema) =>
    ofdBillMode === 'MANUAL' ? schema : schema.required(FormSchemaMessages.required)
  ),
  ofdName: string().when(['ofdBillMode'], ([ofdBillMode], schema) =>
    ofdBillMode === 'MANUAL' ? schema : schema.required(FormSchemaMessages.required)
  ),
})

export const CreateTemplateDialog = (): React.ReactElement => {
  const { addSnack } = useSnackbarContext()
  const qc = useQueryClient()
  const { showDialog, handleShowDialog, handleHideDialog } = useDialog()
  const { mutateAsync, reset } = useMutateCreateTemplate()
  const user = useUser()

  const handleCreateTemplateSubmit = async ({
    ofdPaymentMethodType,
    ofdPaymentSubjectType,
    ofdName,
    ofdNomenclatureCode,
    ...rest
  }: CreateTemplateRequestParams) => {
    try {
      await mutateAsync({
        ...rest,
        agreementNeeded: rest.agreementNeeded === 'true',
        addressNeeded: rest.addressNeeded === 'true',
        amountUnitsNeeded: rest.amountUnitsNeeded === 'true',
        freeSumAvailable: rest.freeSumAvailable === 'true',
        ...(rest.ofdBillMode === 'AUTO'
          ? { ofdNomenclatureCode, ofdPaymentSubjectType, ofdPaymentMethodType, ofdName }
          : {}),
      })

      addSnack({
        severity: 'success',
        message: 'Страница успешно создана',
        timeout: 6000,
      })

      qc.invalidateQueries({ queryKey: [TEMPLATES_QUERY_KEYS.list] })
      handleHideDialog()
      reset()
    } catch (axiosError) {
      let errorMessage = 'Произошла непредвиденная ошибка'

      const error = axiosError as AxiosError<ApiErrorResponse>
      if (error.isAxiosError && error.response?.data?.errorMessage) {
        errorMessage = error.response.data.errorMessage
      }

      addSnack({
        message: errorMessage,
        severity: 'error',
        timeout: 3000,
      })
      console.log(axiosError)
    }
  }

  return (
    <Box display="flex" justifyContent="flex-end">
      <Button onClick={handleShowDialog}>Создать ссылку на оплату</Button>
      {showDialog && (
        <Form<CreateTemplateRequestParams>
          name={TEMPLATE_FORM}
          onSubmit={handleCreateTemplateSubmit}
          validationSchema={templateFormValidationSchema}
          initialValues={{
            ofdBillMode: 'MANUAL',
            ofdPaymentMethodType: 'FULL_PREPAYMENT',
            ofdPaymentSubjectType: 'GOOD',
            addressNeeded: 'false',
            agreementNeeded: 'false',
            amountUnitsNeeded: 'false',
            freeSumAvailable: 'false',
          }}
        >
          <Dialog
            title="Создание статической страницы оплаты"
            maxWidth="md"
            open={showDialog}
            onClose={handleHideDialog}
            content={
              <Grid container flexDirection="column" spacing={2} mt={2}>
                <Grid item xs={12}>
                  <TextFieldControl<CreateTemplateRequestParams>
                    name="paymentInfoShort"
                    fullWidth
                    label="Название товара/услуги"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextFieldControl<CreateTemplateRequestParams>
                    name="paymentInfoFull"
                    fullWidth
                    label="Описание товара/услуги"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextFieldControl<CreateTemplateRequestParams>
                    name="amount"
                    fullWidth
                    normalize={InputNormalizers.positiveNumbersOnly()}
                    label="Сумма"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextFieldControl<CreateTemplateRequestParams>
                    name="paymentCountLimit"
                    fullWidth
                    normalize={InputNormalizers.digitsOnly}
                    label="Кол-во возможных платежей по данному шаблону"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography sx={{ mt: 2 }} variant="h5">
                    Доступность ссылки
                  </Typography>
                </Grid>
                <Grid container item flexDirection="row" spacing={2}>
                  <Grid item xs={4}>
                    <TextFieldControl<CreateTemplateRequestParams>
                      name="validDays"
                      fullWidth
                      normalize={InputNormalizers.digitsOnly}
                      label="Дни"
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <TextFieldControl<CreateTemplateRequestParams>
                      name="validHours"
                      fullWidth
                      normalize={InputNormalizers.digitsOnly}
                      label="Часы"
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <TextFieldControl<CreateTemplateRequestParams>
                      name="validMinutes"
                      fullWidth
                      normalize={InputNormalizers.digitsOnly}
                      label="Минуты"
                    />
                  </Grid>
                </Grid>
                <Grid item xs={12}>
                  <RadioGroupControl<CreateTemplateRequestParams>
                    name="addressNeeded"
                    label="Адреса доставки при создании платежа"
                    row
                    items={[
                      { title: 'Обязательный', value: 'true' },
                      { title: 'Не обязательный', value: 'false' },
                    ]}
                  />
                </Grid>
                <Grid item xs={12}>
                  <RadioGroupControl<CreateTemplateRequestParams>
                    name="agreementNeeded"
                    row
                    label="Номер договора при создании платежа"
                    items={[
                      { title: 'Обязательный', value: 'true' },
                      { title: 'Не обязательный', value: 'false' },
                    ]}
                  />
                </Grid>
                <Grid item xs={12}>
                  <RadioGroupControl<CreateTemplateRequestParams>
                    name="amountUnitsNeeded"
                    row
                    label="Указание количества при создании платежа"
                    items={[
                      { title: 'Обязательно', value: 'true' },
                      { title: 'Не обязательно', value: 'false' },
                    ]}
                  />
                </Grid>
                <Grid item xs={12}>
                  <RadioGroupControl<CreateTemplateRequestParams>
                    name="freeSumAvailable"
                    row
                    label="Доступность ввода любой суммы при создании платежа"
                    items={[
                      { title: 'Да', value: 'true' },
                      { title: 'Нет', value: 'false' },
                    ]}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextFieldControl<CreateTemplateRequestParams>
                    name="productCode"
                    fullWidth
                    label="Выбранный продукт для страницы оплаты"
                  />
                </Grid>
                {user?.permissions?.ofd && (
                  <>
                    <Grid item xs={12}>
                      <TextFieldControl<CreateTemplateRequestParams>
                        name="unit"
                        fullWidth
                        label="Единица измерения для чека"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <RadioGroupControl<CreateOperationRequestParams>
                        name="ofdBillMode"
                        revalidateFields={[
                          'ofdPaymentMethodType',
                          'ofdNomenclatureCode',
                          'ofdPaymentSubjectType',
                          'ofdName',
                        ]}
                        row
                        label="Режим формирования чека"
                        items={[
                          { title: 'Использовать указанные ниже', value: 'AUTO' },
                          { title: 'Не используется', value: 'MANUAL' },
                        ]}
                      />
                    </Grid>
                    <OfdFields />
                  </>
                )}
              </Grid>
            }
            actions={<SubmitButton form={TEMPLATE_FORM}>Создать</SubmitButton>}
          />
        </Form>
      )}
    </Box>
  )
}
